import React, { useEffect } from "react";
import { Header } from "../../components/Header";
import { Footer } from "../../components/Footer";
import { injectJsonLd, breadcrumbSchema } from "../../seo";

const platforms = [
  { name: "Shopify", description: "Add the chat widget to your Shopify storefront in one paste." },
  { name: "WordPress", description: "Works with any WordPress theme — no plugin required." },
  { name: "Wix", description: "Paste the script tag in Wix's custom code section and go live." },
  { name: "Custom HTML", description: "Any website with a <body> tag can embed Onyx AI." },
  { name: "React / Next.js", description: "Drop the script tag in your _document or layout component." },
  { name: "Webflow", description: "Embed via Webflow's custom code panel — zero friction." },
];

const widgetFeatures = [
  {
    title: "Embeddable Chat Widget",
    description:
      "One script tag adds a fully functional AI chatbot to any website. The widget is lightweight, mobile-responsive, and loads asynchronously.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
      </svg>
    ),
  },
  {
    title: "Embeddable Agent Widget",
    description:
      "Let your CS team respond from any system. Embed a lightweight agent console into your CRM, helpdesk, or internal tools with one script tag.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
      </svg>
    ),
  },
  {
    title: "Multi-Tenant Architecture",
    description:
      "Each client gets isolated data, unique API keys, and custom configurations. Perfect for agencies managing multiple brands.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3H21m-3.75 3H21" />
      </svg>
    ),
  },
  {
    title: "REST API Access",
    description:
      "Advanced users can call the chat and FAQ APIs directly with their unique API key. Full documentation available at /docs.",
    icon: (
      <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M13.19 8.688a4.5 4.5 0 011.242 7.244l-4.5 4.5a4.5 4.5 0 01-6.364-6.364l1.757-1.757m13.35-.622l1.757-1.757a4.5 4.5 0 00-6.364-6.364l-4.5 4.5a4.5 4.5 0 001.242 7.244" />
      </svg>
    ),
  },
];

export function IntegrationsPage() {
  useEffect(() => {
    document.title = "Chatbot Integrations — Onyx AI";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "Integrate Onyx AI with Shopify, WordPress, Wix, and any website. Embed via one script tag or connect via REST API. Works everywhere."
      );
    }
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", "https://onyxai.chat/features/integrations");
    }
    return injectJsonLd("breadcrumb-jsonld", breadcrumbSchema([
      { name: "Home", path: "/" },
      { name: "Features", path: "/features" },
      { name: "Integrations", path: "/features/integrations" },
    ]));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a12]">
      <Header />
      <main className="pt-32 pb-24 px-6">
        <div className="max-w-5xl mx-auto">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-[#6a5a7a] mb-10">
            <a href="/" className="hover:text-[#a078d0] transition-colors">Home</a>
            <span>/</span>
            <a href="/features" className="hover:text-[#a078d0] transition-colors">Features</a>
            <span>/</span>
            <span className="text-[#8a7a9a]">Integrations</span>
          </nav>

          {/* Hero */}
          <div className="mb-16">
            <p className="text-[#a078d0] text-sm font-medium uppercase tracking-widest mb-4">
              Integrations
            </p>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-5">
              Works With Every Platform You Already Use
            </h1>
            <p className="text-[#8a7a9a] text-lg max-w-2xl">
              One script tag. Any website. Onyx AI slots into your existing stack without forcing you to change tools or hire a developer.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="/signup"
                className="inline-block px-7 py-3 bg-[#7b5ea7] hover:bg-[#6a4f96] text-white font-medium rounded-lg transition-colors"
              >
                Get Your Embed Code
              </a>
              <a
                href="/docs"
                className="inline-block px-7 py-3 border border-[#2a2040] hover:border-[#a078d0] text-[#8a7a9a] hover:text-white font-medium rounded-lg transition-colors"
              >
                View API Docs
              </a>
            </div>
          </div>

          {/* Platform compatibility */}
          <h2 className="text-2xl font-bold text-white mb-6">Supported Platforms</h2>
          <div className="grid md:grid-cols-3 gap-4 mb-16">
            {platforms.map((p) => (
              <div key={p.name} className="p-5 rounded-xl onyx-card flex gap-3 items-start">
                <div className="w-2 h-2 mt-2 rounded-full bg-[#a078d0] flex-shrink-0" />
                <div>
                  <p className="text-white font-semibold text-sm mb-1">{p.name}</p>
                  <p className="text-[#8a7a9a] text-xs leading-relaxed">{p.description}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Widget & API features */}
          <h2 className="text-2xl font-bold text-white mb-6">Integration Capabilities</h2>
          <div className="grid md:grid-cols-2 gap-6">
            {widgetFeatures.map((item) => (
              <div key={item.title} className="p-6 rounded-xl onyx-card onyx-glow-hover">
                <div className="w-10 h-10 rounded-lg bg-[#a078d0]/10 flex items-center justify-center text-[#c0a0e0] mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-[#8a7a9a] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
